import {
  createContext,
  useContext,
  useState,
  type ReactNode,
  useRef,
  useEffect,
  useMemo,
} from 'react'

import {
  motion,
  type MotionProps,
  AnimatePresence,
  type Transition,
  type AnimatePresenceProps,
} from 'framer-motion'

type StaggerContextType = {
  registerItem: (id: string) => number
  markItemAsVisible: (id: string) => void
  getQueuePosition: (id: string) => number
  staggerDelay: number
}

const StaggerContext = createContext<StaggerContextType | null>(null)

export interface StaggerRootProps extends AnimatePresenceProps {
  children: ReactNode
  staggerDelay?: number
}

export interface StaggerItemProps extends Omit<MotionProps, 'transition'> {
  as?: keyof HTMLElementTagNameMap
  children: ReactNode
  staggerId?: string
  transition?: Transition
}

const StaggerRoot = ({
  children,
  staggerDelay = 0.25,
  ...props
}: StaggerRootProps) => {
  const [visibleItems, setVisibleItems] = useState<Set<string>>(new Set())
  const queueRef = useRef<Map<string, number>>(new Map())
  const nextPositionRef = useRef(0)

  const registerItem = (id: string) => {
    if (!queueRef.current.has(id)) {
      const position = nextPositionRef.current++
      queueRef.current.set(id, position)
      return position
    }
    return queueRef.current.get(id) || 0
  }

  const markItemAsVisible = (id: string) => {
    setVisibleItems((prev) => {
      const newSet = new Set(prev)
      newSet.add(id)
      return newSet
    })
  }

  const getQueuePosition = (id: string) => {
    if (visibleItems.has(id)) return 0

    const position = queueRef.current.get(id)
    if (position !== undefined) {
      return position
    }

    return registerItem(id)
  }

  useEffect(() => {
    if (children) {
      queueRef.current.clear()
      nextPositionRef.current = 0
      setVisibleItems(new Set())
    }
  }, [children])

  const contextValue = useMemo(
    () => ({
      registerItem,
      markItemAsVisible,
      getQueuePosition,
      staggerDelay,
    }),
    [staggerDelay, visibleItems],
  )

  return (
    <StaggerContext.Provider value={contextValue}>
      <AnimatePresence {...props}>{children}</AnimatePresence>
    </StaggerContext.Provider>
  )
}

const StaggerItem = ({
  children,
  staggerId,
  transition,
  ...props
}: StaggerItemProps) => {
  const MotionComponent = props.as ? motion[props.as] : motion.div
  const context = useContext(StaggerContext)
  const [id] = useState(() => staggerId || `stagger-${Math.random()}`)
  const [position, setPosition] = useState<number | null>(null)

  useEffect(() => {
    if (context && position === null) {
      const newPosition = context.registerItem(id)
      setPosition(newPosition)
    }
  }, [id, context, position])

  if (!context) {
    throw new Error('StaggerItem must be used within a StaggerRoot')
  }

  const queuePosition =
    position !== null ? position : context.getQueuePosition(id)

  return (
    <MotionComponent
      onAnimationComplete={() => context.markItemAsVisible(id)}
      transition={{
        ...transition,
        delay: (transition?.delay || 0) + queuePosition * context.staggerDelay,
      }}
      {...props}
    >
      {children}
    </MotionComponent>
  )
}

/**
 * StaggerAnimation stagger the animation of child components by modifying their delay
 * based on the index of the item in the children array. This component also handles items
 * that gets mounted after the initial render.
 *
 * @example
 * <StaggerAnimation.Root>
 *   <StaggerAnimation.Item {...framerMotionProps}>...</StaggerAnimation.Item>
 *   <StaggerAnimation.Item {...framerMotionProps}>...</StaggerAnimation.Item>
 * </StaggerAnimation.Root>
 */
export const StaggerAnimation = {
  Root: StaggerRoot,
  Item: StaggerItem,
}
